import { SearchOutlined } from '@ant-design/icons';
import { Col, Input, Pagination, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import SpravovaneLeiCard from 'components/molecules/Lei/SpravovaneLeiCard';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import leiPrehledUctuApi, { ILeiPrehledUCtu } from 'services/lei/PrehledUctu';

import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

const PrehledUctu: React.FC = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { language, aktivniFirma } = useContext(Context);
    const { setLevel } = useProfily();
    const [searching, setSearching] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [leiUcty, setLeiUcty] = useState<ILeiPrehledUCtu>();
    const [errStatus, setErrStatus] = useState<number>();
    const [paginationPage, setPaginationPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<string>('');
    const { FIRMA_PLAIN, LEI_DETAIL } = routePathEnum;
    const baseURL = `${FIRMA_PLAIN}${aktivniFirma?.iducet}`;

    useEffect(() => {
        setLoading(true);
        setErrStatus(0);
        setLevel('lei', null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        /* funkcionalita filtru api sa zavola po 900ms bez inputu alebo hned 
        ak input === '' */
        const delayDebounceFn = setTimeout(
            () => {
                setSearching(true);
                leiPrehledUctuApi(searchValue)
                    .then(data => {
                        setPaginationPage(1);
                        setLeiUcty(data);
                    })
                    .catch(
                        err =>
                            err.response.status &&
                            err.response.status === 400 &&
                            !searchValue &&
                            navigateWorkaround(routePathEnum.MAIN_PAGE)
                    )
                    .finally(() => {
                        setSearching(false);
                        setLoading(false);
                    });
            },
            searchValue ? 900 : 0
        );
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    return (
        <LoadingWrapper loading={loading}>
            {errStatus && !leiUcty ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        {errStatus === 404 ? (
                            <>
                                <Title text="ZADNE_LEI" level={3} />{' '}
                                <Paragraph
                                    text="LEI_NONE_INFO"
                                    style={{
                                        marginBottom: 8
                                    }}
                                />
                            </>
                        ) : errStatus ? (
                            <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                        ) : null}
                    </Col>
                </Row>
            ) : (
                <>
                    <Title level={3} text="LEI_SPRAVOVANE" />

                    <Paragraph
                        text="LEI_NONE_INFO"
                        style={{
                            marginBottom: 8
                        }}
                    />

                    <Input.Search
                        placeholder={getLanguageObject(language).MAIN_PAGE_FILTER_PLACEHOLDER}
                        allowClear={true}
                        enterButton={getLanguageObject(language).VYHLEDAT}
                        size="large"
                        onChange={ev => setSearchValue(ev.target.value)}
                        bordered={false}
                        loading={searching}
                        id="prehledUctuLeiSearch"
                        style={{
                            backgroundColor: 'white',
                            border: '1px solid #D4D6D6',
                            padding: '5px',
                            borderRadius: 6
                        }}
                        className="search-input"
                        prefix={<SearchOutlined style={{}} />}
                    />

                    <Row gutter={8}>
                        {leiUcty?.lei
                            .slice((paginationPage - 1) * 21, (paginationPage - 1) * 21 + 21)
                            .map((ucet, i) => {
                                return (
                                    <SpravovaneLeiCard
                                        ucet={ucet}
                                        onClick={() => {
                                            setLevel('lei', {
                                                selectedLei: ucet,
                                                idVarsymZadosti: {
                                                    varsym: ucet.varsymbol
                                                }
                                            });
                                            window.scrollTo(0, 0);
                                            navigateWorkaround(`${baseURL}${LEI_DETAIL}`);
                                        }}
                                        key={i}
                                        id={`SpravovaneLeiCard${i}`}
                                    />
                                );
                            })}
                    </Row>
                    <Pagination
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}
                        total={leiUcty?.lei.length}
                        onChange={page => setPaginationPage(page)}
                        pageSize={21}
                        current={paginationPage}
                        showSizeChanger={false}
                    />
                </>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!modalMessage}
                onCancel={() => setModalMessage('')}
                afterClose={() => {
                    setModalMessage('');
                }}
                footer={
                    <Row>
                        <ButtonComponent
                            id="leiPrehledUctuZavritBtn"
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalMessage('');
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={modalMessage} />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default PrehledUctu;
